import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { Theme, SystemStyleObject } from '@mui/system';
import useGetContragentTypes from '~/modules/identification/hooks/useGetContragentTypes';
import { theme } from '~/theme';
import { styles } from '../../../styles';
import { TableHeader } from '~/components/ListCardTable/TableHeader';
import { clarifyingInfoColumns } from '../../../constants';
import { ClarifyingInfoRow } from './ClarifyingInfoRow/ClarifyingInfoRow';
import { TPurposeOfBusinessRelations } from '../../../types';
import { useFormContext } from 'react-hook-form';
import { useExplanationQuestionsQuery } from '~/queries/useExplanationQuestionsQuery';
import { TExplanationQuestion } from '~/queries/types';
import { commonStyles } from '~/theme/styles/commonStyles';
import { BaseModal } from '~/components/modals/BaseModal/BaseModal.tsx';

interface IClarifyingInfoModalProps {
  isOpened: boolean;
  handleClose: () => void;
}

export const ClarifyingInfoModal = ({ isOpened, handleClose }: IClarifyingInfoModalProps) => {
  const { isLegalResident } = useGetContragentTypes();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { questions } = useExplanationQuestionsQuery();

  const {
    getValues,
    trigger,
    formState: { errors },
    setFocus,
  } = useFormContext<TPurposeOfBusinessRelations>();

  const handleContinue = async () => {
    const isValid = await trigger('explanation');
    if (!isValid) {
      const firstItemIndex = errors?.explanation?.findIndex?.(Boolean);
      if (typeof firstItemIndex === 'number') {
        const answers = errors?.explanation?.[firstItemIndex]?.answers;
        const isAnswer = errors?.explanation?.[firstItemIndex]?.answers?.message;
        if (isAnswer) {
          setFocus(`explanation.${firstItemIndex}.answers`, { shouldSelect: true });
        } else {
          const { questionId } = getValues(`explanation.${firstItemIndex}`) || {};
          const answerErrorIndex = answers?.findIndex?.(Boolean) || 0;
          const commentErrorIndex =
            answers?.[answerErrorIndex]?.comments?.findIndex?.(Boolean) || 0;
          const isFullName = answers?.[answerErrorIndex]?.comments?.[commentErrorIndex]?.fullName;
          const isRNOKPP = answers?.[answerErrorIndex]?.comments?.[commentErrorIndex]?.RNOKPP;
          const isGeneral = answers?.[answerErrorIndex]?.comments?.[commentErrorIndex]?.general;
          const focusItem = `explanation.${firstItemIndex}.answers.${answerErrorIndex}.comments.${commentErrorIndex}.${(isRNOKPP && 'RNOKPP') || (isFullName && 'fullName') || (isGeneral && 'general')}`;

          if (questionId === 9) {
            setFocus(focusItem as keyof typeof answers, { shouldSelect: true });
          } else {
            setFocus(focusItem as keyof typeof answers, {
              shouldSelect: true,
            });
          }
        }
      }
      return;
    }
    handleClose();
  };

  return (
    <BaseModal
      open={isOpened}
      onClose={handleClose}
      containerProps={{ sx: styles.clarifyingModalContainer }}
    >
      <Typography variant="h6">Уточнююча інформація щодо зміста та суті діяльності</Typography>
      {isLegalResident && (
        <Typography variant="body2" align="center">
          Надайте інформацію щодо: місця проведення господарської діяльності з врахуванням суті і
          характеру діяльності, наявність орендованого чи власного
          приміщення/офісу/складів/виробничих приміщень і т.д. та інших активів, достатніх для
          ведення відповідного виду господарської діяльності, наявність свого чи орендованого
          автотранспорту, з якими контрагентами укладені або будуть укладені договори на поставку чи
          реалізацію товару/продукції, з якими запланована в майбутньому співпраця. Додатково
          необхідно додати копії договорів (драфти), підтверджуючих наявність таких приміщень
          (оренди/власності); копії договорів (драфти) з контрагентами та документи, що
          підтверджують джерела походження коштів КБВ/засновників у розділ ДОКУМЕНТИ.
        </Typography>
      )}
      <Box sx={[styles.table as SystemStyleObject<Theme>, { minHeight: 150 }]}>
        {!isMobile && <TableHeader columns={clarifyingInfoColumns} position="static" />}
        <Box sx={{ ...commonStyles.columnFlexContainer, gap: { sm: 2, md: 0, xs: 2 } }}>
          {(questions as TExplanationQuestion[])?.map((question, index) => (
            <ClarifyingInfoRow key={question.id} index={index} question={question} />
          ))}
        </Box>
      </Box>
      <Button variant="contained" onClick={handleContinue}>
        Продовжити
      </Button>
    </BaseModal>
  );
};
